import { ModelOrigin } from 'src/app/interfaces/api/model';
import { GPS } from 'src/app/interfaces/shared/gps';
import { XYZ } from 'src/app/interfaces/shared/xyz';
import * as THREE from 'three';

const crossProduct = (a: number[], b: number[]) => [(a[1] ?? 0) * (b[2] ?? 0) - (a[2] ?? 0) * (b[1] ?? 0), (a[2] ?? 0) * (b[0] ?? 0) - (a[0] ?? 0) * (b[2] ?? 0), (a[0] ?? 0) * (b[1] ?? 0) - (a[1] ?? 0) * (b[0] ?? 0)]

const distance = (vec1: string | any[], vec2: number[]) => {
    let sum = 0;
    for (let i = 0; i < vec1.length; i++)
        sum += ((vec1[i] ?? 0) - (vec2[i] ?? 0)) ** 2;
    return Math.sqrt(sum);
}

const filterVerticesByZ = (faces: string | any[], vertices: string | any[], z = 0) => {
    let v, f, i, il;
    let indices: { [key: number]: number } = {};
    let newVertices = [], newFaces = [];
    for (i = 0, il = vertices.length; i < il; i++) {
        v = vertices[i];

        if (v.z > z) {
            newVertices.push(v);
            indices[i] = newVertices.length - 1;
        }
    }

    for (i = 0, il = faces.length; i < il; i++) {
        f = faces[i];

        if (indices[f.a] !== undefined && indices[f.b] !== undefined && indices[f.c] !== undefined) {
            f.a = indices[f.a];
            f.b = indices[f.b];
            f.c = indices[f.c];
            newFaces.push(f)
        }
    }
    return { faces: newFaces, vertices: newVertices };
}

//copied from THREEJS library
const mergeVertices = (faces: any[], vertices: string | any[], decimal = 3) => {

    let verticesMap: { [key: string]: number } = {}; // Hashmap for looking up vertices by position coordinates (and making sure they are unique)
    let unique = [], changes: any[] = [];

    let v, key;
    const precisionPoints = decimal; // number of decimal points, e.g. 4 for epsilon of 0.0001
    const precision = Math.pow(10, precisionPoints);
    let i, il, face;
    let indices, j, jl;

    for (i = 0, il = vertices.length; i < il; i++) {
        v = vertices[i];
        key = Math.round(v.x * precision) + '_' + Math.round(v.y * precision) + '_' + Math.round(v.z * precision);

        if (verticesMap[key] === undefined) {
            verticesMap[key] = i;
            unique.push(vertices[i]);
            changes[i] = unique.length - 1;
        } else {
            changes[i] = changes[verticesMap[key] ?? 0];
        }
    }

    let faceIndicesToRemove = [];

    for (i = 0, il = faces.length; i < il; i++) {
        face = faces[i];
        face.a = changes[face.a];
        face.b = changes[face.b];
        face.c = changes[face.c];
        indices = [face.a, face.b, face.c];

        for (let n = 0; n < 3; n++) {
            if (indices[n] === indices[(n + 1) % 3]) {
                faceIndicesToRemove.push(i);
                break;
            }
        }
    }

    for (i = faceIndicesToRemove.length - 1; i >= 0; i--) {
        const idx = faceIndicesToRemove[i];
        faces.splice(idx ?? 0, 1);
    }

    // Use unique set of vertices
    const diff = vertices.length - unique.length;
    vertices = unique;
    return { vertices, faces, diff };
}

const paralleleLine = (A: number[], B: number[], dist: number) => {
    const vectorAB = [(B[0] ?? 0) - (A[0] ?? 0), (B[1] ?? 0) - (A[1] ?? 0)];
    const normAB = Math.sqrt((vectorAB[0] ?? 0) ** 2 + (vectorAB[1] ?? 0) ** 2);
    const normalizedVector = [(vectorAB[0] ?? 0) / normAB, (vectorAB[1] ?? 0) / normAB];
    const orthogonalVector = [-(normalizedVector[1] ?? 0), (normalizedVector[0] ?? 0)];
    const offset_vector = [(orthogonalVector[0] ?? 0) * dist, (orthogonalVector[1] ?? 0) * dist];
    const C = [(A[0] ?? 0) + (offset_vector[0] ?? 0), (A[1] ?? 0) + (offset_vector[1] ?? 0)];
    const D = [(C[0] ?? 0) + (vectorAB[0] ?? 0), (C[1] ?? 0) + (vectorAB[1] ?? 0)]
    const lineCD = [C, D];
    return lineCD;
}

const findIntersection = (line1: any[][], line2: any[][], dec = 2, maxDist: number) => {
    const x1 = line1[0]?.[0] ?? 0;
    const y1 = line1[0]?.[1] ?? 0;
    const x2 = line1[1]?.[0] ?? 0;
    const y2 = line1[1]?.[1] ?? 0;
    const x3 = line2[0]?.[0] ?? 0;
    const y3 = line2[0]?.[1] ?? 0;
    const x4 = line2[1]?.[0] ?? 0;
    const y4 = line2[1]?.[1] ?? 0;

    //to use for parallele line
    const mean = [(x2 + x3) * .5, (y2 + y3) * .5];

    let mAB = x2.toFixed(dec) !== x1.toFixed(dec) ? (y2 - y1) / (x2 - x1) : Infinity;
    let mCD = x3.toFixed(dec) !== x4.toFixed(dec) ? (y4 - y3) / (x4 - x3) : Infinity;

    if (Math.abs(mAB) < 1 * 10 ** -dec) mAB = 0;
    if (Math.abs(mCD) < 0.01) mCD = 0;

    //if parallele
    if (Math.abs(Math.abs(mAB) - Math.abs(mCD)) < 0.01) return mean;

    const bAB = y1 - mAB * x1;
    const bCD = y3 - mCD * x3;

    //if parrallele to abscisse ?
    if (mAB == Infinity) {
        return [x1, mCD * x1 + bCD]
    }

    if (mCD == Infinity) {
        return [x3, mAB * x3 + bAB]
    }

    let x = (bCD - bAB) / (mAB - mCD);
    let y = mAB * x + bAB;
    // if (y4.toFixed(dec) == y3.toFixed(dec)) y = y3;
    // if (y2.toFixed(dec) == y1.toFixed(dec)) y = y1;
    const result = [x, y];
    const dist = distance(result, mean);

    return maxDist && dist > maxDist ? mean : result;
}

const lookAt = (position: { angleTo: (arg0: any) => any; y: number; }, target: { y: number; }, up?: any) => {
    let vector, angle;

    angle = position.angleTo(target)
    if (target.y < position.y) angle *= -1
    return angle
}

const getLatLonFromXYZ = (x: number, y: number, z: number): [number, number] => {
    let r = 6378137 //earth radius in meters
    let lon = ((x / (Math.PI / 180)) / Math.cos(y / r)) / (r);
    let lat = (y / (Math.PI / 180)) / (r);

    return [lon, lat];
}

/**
     * transform lat lon to x,y coordinates
     *
     */
const getXYFromLatLonAlt = (lat: number, lon: number, alt = 0): [number, number] => {
    let r = 6378137; //earth radius in meters
    let x: number = (r) * (lon * Math.PI / 180) * Math.cos(lat * Math.PI / 180);
    let y: number = (r) * (lat * Math.PI / 180);

    return [x, y];
}


const degToRad = (deg: number) => deg * Math.PI / 180;
const radToDeg = (rad: number) => rad * 180 / Math.PI;

const getXYFromLatLonAlt2 = (lat: number, lon: number, alt: number = 0, originLat = 0, originLon = 0): [number, number] => {
    const R = 6378137; // Earth radius in meters
    const dLat = degToRad(lat - originLat);
    const dLon = degToRad(lon - originLon);
    const originLatRad = degToRad(originLat);

    const x = R * dLon * Math.cos(originLatRad);
    const y = R * dLat;
    return [x, y];
}

const getLatLonFromXYZ2 = (x: number, y: number, z = 0, originLat = 0, originLon = 0): [number, number] => {
    const R = 6378137;
    const dLat = y / R;
    const dLon = x / (R * Math.cos(degToRad(originLat)));

    const lat = originLat + radToDeg(dLat);
    const lon = originLon + radToDeg(dLon);
    return [lon, lat];
}


const axisToOrientationVector = (axis: string): THREE.Vector3 => {
    let orientation = new THREE.Vector3(0, 0, 0);

    switch (axis) {
        case 'x':
            orientation = new THREE.Vector3(1, 0, 0);
            break;
        case 'y':
            orientation = new THREE.Vector3(0, 1, 0);
            break;
        case 'z':
            orientation = new THREE.Vector3(0, 0, 1);
            break;
        default:
            break;
    }

    return orientation;
}

const isGPS = (pos: GPS | XYZ) => {
    return 'lon' in pos && 'lat' in pos;
}

const applyTransform2 = (pos: GPS | XYZ,
    origin: ModelOrigin,
    offset: { x: number; y: number; z: number; },
    scale?: number) => {
    let vec3 = new THREE.Vector3(0, 0, 0);

    if (isGPS(pos)) {
        // Extraire lat/lon de l'origine projet depuis projectLatLonXY
        // (en supposant que vous stockez aussi originLat/originLon dans origin)
        const originLat = origin.position.lat ?? 0;
        const originLon = origin.position.lon ?? 0;

        // Calcule directement le delta XY par rapport à l'origine
        const pointXY = getXYFromLatLonAlt2(pos.lat, pos.lon, pos.z, originLat, originLon);

        // Plus besoin de soustraire projectLatLonXY, le delta est déjà relatif
        vec3.x = (pointXY[0] ?? 0) - (origin.projectXY![0] ?? 0);
        vec3.y = (pointXY[1] ?? 0) - (origin.projectXY![1] ?? 0);
    } else {
        vec3.x = pos.x! - (origin.projectXY![0] ?? 0) - (origin.projectLatLonXY![0] ?? 0);
        vec3.y = pos.y! - (origin.projectXY![1] ?? 0) - (origin.projectLatLonXY![1] ?? 0);
    }

    vec3.z = pos.z;
    scale = scale ?? (origin.scale || 1);
    let angle = origin.angle || 0;
    vec3.z -= parseFloat(String(origin.position?.z || 0));
    vec3.x *= scale || 1;
    vec3.y *= scale || 1;
    vec3.z *= scale || 1;
    vec3.x -= offset.x;
    vec3.y -= offset.y;
    vec3.z -= offset.z;

    if (angle) {
        const radians = parseFloat(String(angle)) * (Math.PI / 180);
        vec3.applyAxisAngle(axisToOrientationVector(origin.axis!), radians);
    } else {
        vec3.applyAxisAngle(axisToOrientationVector("x"), (origin.xAngle || 0) * Math.PI / 180);
        vec3.applyAxisAngle(axisToOrientationVector("y"), (origin.yAngle || 0) * Math.PI / 180);
        vec3.applyAxisAngle(axisToOrientationVector("z"), (origin.zAngle || 0) * Math.PI / 180);
    }

    return { x: vec3.x, y: vec3.y, z: vec3.z };
}
const reverseTransform2 = (
    pos: XYZ,
    origin: ModelOrigin,
    offset: { x: number; y: number; z: number },
    scale?: number,
) => {
    let vec3 = new THREE.Vector3(pos.x, pos.y, pos.z);
    scale = scale ?? (origin.scale || 1);
    const angle = parseFloat(String(origin.angle)) || 0;

    // 1. Undo rotation (reverse order, negated angles)
    if (angle) {
        const radians = angle * (Math.PI / 180) * -1;
        vec3.applyAxisAngle(axisToOrientationVector(origin.axis!), radians);
    } else {
        vec3.applyAxisAngle(axisToOrientationVector("z"), (origin.zAngle || 0) * -1 * Math.PI / 180);
        vec3.applyAxisAngle(axisToOrientationVector("y"), (origin.yAngle || 0) * -1 * Math.PI / 180);
        vec3.applyAxisAngle(axisToOrientationVector("x"), (origin.xAngle || 0) * -1 * Math.PI / 180);
    }

    // 2. Undo offset subtraction
    vec3.x += offset.x;
    vec3.y += offset.y;
    vec3.z += offset.z;

    // 3. Undo scale
    vec3.x /= scale;
    vec3.y /= scale;
    vec3.z /= scale;

    // 4. Undo z position subtraction
    vec3.z += parseFloat(String(origin.position?.z || 0));

    // 5. Undo: vec3 = pointXY - projectXY  →  pointXY = vec3 + projectXY
    vec3.x += (origin.projectXY![0] ?? 0);
    vec3.y += (origin.projectXY![1] ?? 0);

    // 6. Convert XY back to GPS using the same origin as applyTransform2
    const originLat = origin.position.lat ?? 0;
    const originLon = origin.position.lon ?? 0;
    const pointLonLat = getLatLonFromXYZ2(vec3.x, vec3.y, vec3.z, originLat, originLon);

    return {
        lon: pointLonLat[0]!,
        lat: pointLonLat[1]!,
        x: vec3.x,
        y: vec3.y,
        z: vec3.z,
    };
}
const applyTransform = (pos: GPS | XYZ, origin: { projectLatLonXY?: number[]; projectXY?: number[]; scale?: number; angle?: string | number; position?: { z?: string | number; }; axis?: string; xAngle?: number; yAngle?: number; zAngle?: number; }, offset: { x: number; y: number; z: number; }, scale?: number) => {

    let vec3 = new THREE.Vector3(0, 0, 0);

    if (isGPS(pos)) {
        const pointXY = getXYFromLatLonAlt2(pos.lat, pos.lon, pos.z);
        vec3.x = (pointXY[0] ?? 0) - (origin.projectLatLonXY![0] ?? 0) - (origin.projectXY![0] ?? 0);
        vec3.y = (pointXY[1] ?? 0) - (origin.projectLatLonXY![1] ?? 0) - (origin.projectXY![1] ?? 0);
    } else {
        vec3.x = pos.x! - (origin.projectXY![0] ?? 0) - (origin.projectLatLonXY![0] ?? 0);
        vec3.y = pos.y! - (origin.projectXY![1] ?? 0) - (origin.projectLatLonXY![1] ?? 0);
    }
    vec3.z = pos.z;
    scale = scale ?? (origin.scale || 1);
    let angle = origin.angle || 0;
    // vec3.x -= origin.position.x;
    // vec3.y -= origin.position.y;
    vec3.z -= parseFloat(String(origin.position?.z || 0));
    vec3.x *= scale || 1;
    vec3.y *= scale || 1;
    vec3.z *= scale || 1;
    vec3.x -= offset.x;
    vec3.y -= offset.y;
    vec3.z -= offset.z;

    if (angle) {
        const radians = parseFloat(String(angle)) * (Math.PI / 180);
        vec3.applyAxisAngle(axisToOrientationVector(origin.axis!), radians);
    } else {
        vec3.applyAxisAngle(axisToOrientationVector("x"), (origin.xAngle || 0) * Math.PI / 180);
        vec3.applyAxisAngle(axisToOrientationVector("y"), (origin.yAngle || 0) * Math.PI / 180);
        vec3.applyAxisAngle(axisToOrientationVector("z"), (origin.zAngle || 0) * Math.PI / 180);
    }

    return { x: vec3.x, y: vec3.y, z: vec3.z };
}

const reverseTransform = (pos: XYZ,
    origin: ModelOrigin,
    offset: { x: number; y: number; z: number; }) => {


    let vec3 = new THREE.Vector3(pos.x, pos.y, pos.z);
    let scale = origin.scale || 1;
    let angle = parseFloat(String(origin.angle)) || 0;
    if (angle) {
        const radians = parseFloat(String(origin.angle)) * (Math.PI / 180) * -1;
        vec3.applyAxisAngle(axisToOrientationVector(origin.axis!), radians);
    } else {
        vec3.applyAxisAngle(axisToOrientationVector("x"), (origin.xAngle || 0) * -1 * Math.PI / 180);
        vec3.applyAxisAngle(axisToOrientationVector("y"), (origin.yAngle || 0) * -1 * Math.PI / 180);
        vec3.applyAxisAngle(axisToOrientationVector("z"), (origin.zAngle || 0) * -1 * Math.PI / 180);
    }

    vec3.x += offset.x;
    vec3.y += offset.y;
    vec3.z += offset.z;
    vec3.x /= scale || 1;
    vec3.y /= scale || 1;
    vec3.z /= scale || 1;

    vec3.z += parseFloat(String(origin.position?.z || 0));


    vec3.x += (origin.projectLatLonXY![0] || 0);
    vec3.y += (origin.projectLatLonXY![1] || 0);
    const pointLonLat = getLatLonFromXYZ2(vec3.x, vec3.y, 0);

    return { lon: pointLonLat[0]!, lat: pointLonLat[1]!, x: vec3.x!, y: vec3.y!, z: vec3.z! };
}

const getVolume = (viewer: {
    impl: {
        getFragmentProxy: (arg0: any, arg1: any) => any; getRenderProxy: (arg0: any, arg1: any) => any;
    };
    model: {
        getData: () => { (): any; new(): any; instanceTree: any; };
    };
}, dbId: any, scale: any) => {
    let volume = 0;
    const it = viewer.model.getData().instanceTree;

    it.enumNodeFragments(dbId, (fragId: any) => {
        getVertices(viewer, scale, fragId, (p1: any, p2: any, p3: any) => {
            let tVolume = getTriangleVolume(p1, p2, p3);
            if (tVolume && !isNaN(tVolume)) {
                volume += tVolume
            }
        })
    }, true);

    return volume;
}

const getTriangleVolume = (p1: { dot: (arg0: any) => number; }, p2: { cross: (arg0: any) => any; }, p3: any) => {
    return p1.dot(p2.cross(p3)) / 6.0;
}

const getVertices = (viewer: { impl: { getFragmentProxy: (arg0: any, arg1: any) => any; getRenderProxy: (arg0: any, arg1: any) => any; }; model: any; }, scale: any, fragId: any, callback: { (p1: any, p2: any, p3: any): void; (arg0: any, arg1: any, arg2: any): void; }) => {
    const fragProxy = viewer.impl.getFragmentProxy(
        viewer.model,
        fragId);

    const renderProxy = viewer.impl.getRenderProxy(
        viewer.model,
        fragId);

    fragProxy.updateAnimTransform();

    const matrix = new THREE.Matrix4();
    fragProxy.getWorldMatrix(matrix);

    matrix.makeScale(scale, scale, scale)
    const vA = new THREE.Vector3();
    const vB = new THREE.Vector3();
    const vC = new THREE.Vector3();

    const geometry = renderProxy.geometry;

    if (!geometry) {
        callback(vA, vB, vC);
        return;
    }
    const attributes = geometry.attributes;

    if (attributes.index !== undefined) {
        const indices = attributes.index.array || geometry.ib;
        const positions = geometry.vb ? geometry.vb : attributes.position.array;
        const stride = geometry.vb ? geometry.vbstride : 3;
        let offsets = geometry.offsets;

        if (!offsets || offsets.length === 0) {
            offsets = [{ start: 0, count: indices.length, index: 0 }];
        }

        for (let oi = 0, ol = offsets.length; oi < ol; ++oi) {
            const start = offsets[oi].start;
            const count = offsets[oi].count;
            const index = offsets[oi].index;

            for (let i = start, il = start + count; i < il; i += 3) {
                const a = index + indices[i];
                const b = index + indices[i + 1];
                const c = index + indices[i + 2];

                vA.fromArray(positions, a * stride);
                vB.fromArray(positions, b * stride);
                vC.fromArray(positions, c * stride);
                vA.applyMatrix4(matrix);
                vB.applyMatrix4(matrix);
                vC.applyMatrix4(matrix);

                callback(vA, vB, vC)
            }
        }
    }
    else {
        const positions = geometry.vb ? geometry.vb : attributes.position.array;
        const stride = geometry.vb ? geometry.vbstride : 3;

        for (let i = 0, j = 0, il = positions.length; i < il; i += 3, j += 9) {
            let a = i;
            let b = i + 1;
            let c = i + 2;

            vA.fromArray(positions, a * stride);
            vB.fromArray(positions, b * stride);
            vC.fromArray(positions, c * stride);

            vA.applyMatrix4(matrix);
            vB.applyMatrix4(matrix);
            vC.applyMatrix4(matrix);

            callback(vA, vB, vC)
        }
    }
}

const getNearestPoint = (point: number[], zonePoints: any[]) => {
    let maxDistance = 2000; // the nearest point can't be farther than 2km
    let nearestPoint = zonePoints.reduce(function (nearest: { distance: number; } | null, current: { x: number; y: number; }) {
        const distance = Math.sqrt(
            Math.pow((point[0] ?? 0) - current.x, 2) +
            Math.pow((point[1] ?? 0) - current.y, 2)
        );
        if (distance <= maxDistance && (nearest === null || distance < nearest.distance)) {
            return {
                point: current,
                distance: distance
            };
        }
        return nearest;
    }, null);
    return nearestPoint?.point;
}

// Function to rotate a point around the center
function rotatePoint(x: number, y: number, centerX: number, centerY: number, angle: number) {
    let dx = x - centerX;
    let dy = y - centerY;
    let nx = centerX + dx * Math.cos(angle) + dy * Math.sin(angle); // Corrected sign
    let ny = centerY - dx * Math.sin(angle) + dy * Math.cos(angle); // Corrected sign
    return [nx, ny];
}

const rotateCoords = (positions: any, angleDegrees: number) => {
    let northWestXY = getXYFromLatLonAlt2(positions.north, positions.west, 0);
    let northEastXY = getXYFromLatLonAlt2(positions.north, positions.east, 0);
    let southEastXY = getXYFromLatLonAlt2(positions.south, positions.east, 0);
    let southWestXY = getXYFromLatLonAlt2(positions.south, positions.west, 0);

    // Calculate the center of the image
    let centerX = ((northWestXY[0] ?? 0) + (northEastXY[0] ?? 0) + (southEastXY[0] ?? 0) + (southWestXY[0] ?? 0)) / 4;
    let centerY = ((northWestXY[1] ?? 0) + (northEastXY[1] ?? 0) + (southEastXY[1] ?? 0) + (southWestXY[1] ?? 0)) / 4;

    // Convert angle to radians
    const angle = angleDegrees * Math.PI / 180;

    // Rotate each corner around the center
    let newNorthWestXY = rotatePoint((northWestXY[0] ?? 0), (northWestXY[1] ?? 0), centerX, centerY, angle);
    let newNorthEastXY = rotatePoint((northEastXY[0] ?? 0), (northEastXY[1] ?? 0), centerX, centerY, angle);
    let newSouthEastXY = rotatePoint((southEastXY[0] ?? 0), (southEastXY[1] ?? 0), centerX, centerY, angle);
    let newSouthWestXY = rotatePoint((southWestXY[0] ?? 0), (southWestXY[1] ?? 0), centerX, centerY, angle);

    // Convert the new Cartesian coordinates back to lat/lon
    let newNorthWest = getLatLonFromXYZ2((newNorthWestXY[0] ?? 0), (newNorthWestXY[1] ?? 0), 0);
    let newNorthEast = getLatLonFromXYZ2((newNorthEastXY[0] ?? 0), (newNorthEastXY[1] ?? 0), 0);
    let newSouthEast = getLatLonFromXYZ2((newSouthEastXY[0] ?? 0), (newSouthEastXY[1] ?? 0), 0);
    let newSouthWest = getLatLonFromXYZ2((newSouthWestXY[0] ?? 0), (newSouthWestXY[1] ?? 0), 0);

    // Combine the new positions
    let northLat = Math.max((newNorthWest[1] ?? 0), (newNorthEast[1] ?? 0), (newSouthEast[1] ?? 0), (newSouthWest[1] ?? 0));
    let southLat = Math.min((newNorthWest[1] ?? 0), (newNorthEast[1] ?? 0), (newSouthEast[1] ?? 0), (newSouthWest[1] ?? 0));
    let eastLon = Math.max((newNorthWest[0] ?? 0), (newNorthEast[0] ?? 0), (newSouthEast[0] ?? 0), (newSouthWest[0] ?? 0));
    let westLon = Math.min((newNorthWest[0] ?? 0), (newNorthEast[0] ?? 0), (newSouthEast[0] ?? 0), (newSouthWest[0] ?? 0));

    return {
        north: northLat,
        east: eastLon,
        south: southLat,
        west: westLon
    };
}

/**
 * Helper for getFacesFromGeometry
 */
const isIndexedGeometry = (geometry: THREE.BufferGeometry): boolean => {
    return geometry.index != null;
}

/**
 * Extract faces from any THREE.js geometry type
 */
// const getFacesFromGeometry = (geometry: THREE.BufferGeometry): { a: number, b: number, c: number, normal?: THREE.Vector3 }[] => {
//     const faces: { a: number, b: number, c: number, normal?: THREE.Vector3 }[] = [];
//     const position = geometry.getAttribute('position');
//     if (!position) return faces;

//     if (isIndexedGeometry(geometry)) {
//         const index = geometry.getIndex()!;

//         for (let i = 0; i < index.count; i += 3) {
//             const face = {
//                 a: index.getX(i),
//                 b: index.getX(i + 1),
//                 c: index.getX(i + 2),
//                 normal: new THREE.Vector3()
//             };
//             faces.push(face);
//         }
//     } else {
//         for (let i = 0; i < position.count; i += 3) {
//             const face = {
//                 a: i,
//                 b: i + 1,
//                 c: i + 2,
//                 normal: new THREE.Vector3()
//             };
//             faces.push(face);
//         }
//     }

//     // Calculate face normals
//     for (let j = 0; j < faces.length; j++) {
//         const face = faces[j];
//         if (!face) continue;
//         const pointA = new THREE.Vector3(
//             position.getX(face.a),
//             position.getY(face.a),
//             position.getZ(face.a)
//         );
//         const pointB = new THREE.Vector3(
//             position.getX(face.b),
//             position.getY(face.b),
//             position.getZ(face.b)
//         );
//         const pointC = new THREE.Vector3(
//             position.getX(face.c),
//             position.getY(face.c),
//             position.getZ(face.c)
//         );

//         const triangle = new THREE.Triangle(pointA, pointB, pointC);
//         triangle.getNormal(face.normal!);
//     }

//     return faces;
// }

// /**
//  * Extract vertices from any THREE.js geometry type
//  */
// const getVerticesFromGeometry = (geometry: THREE.BufferGeometry): THREE.Vector3[] => {
//     const vertices: THREE.Vector3[] = [];
//     const position = geometry.getAttribute('position');
//     if (!position) return vertices;

//     for (let i = 0; i < position.count; i++) {
//         const vertex = new THREE.Vector3(
//             position.getX(i),
//             position.getY(i),
//             position.getZ(i)
//         );
//         vertices.push(vertex);
//     }

//     return vertices;
// }

export {
    applyTransform, applyTransform2, axisToOrientationVector,
    crossProduct, degToRad, distance,
    filterVerticesByZ, findIntersection,
    // getFacesFromGeometry,getVerticesFromGeometry,
    getLatLonFromXYZ, getLatLonFromXYZ2, getNearestPoint,
    getVolume, getXYFromLatLonAlt, getXYFromLatLonAlt2, lookAt,
    mergeVertices,
    paralleleLine, radToDeg, reverseTransform, reverseTransform2, rotateCoords
};

